import React, {useState, useEffect} from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import AddressComponent from "../../../Address/Address"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {
	BusinessAnnualRevenue,
	BusinessNumberOfEmployees,
	BusinessVertical,
	CashFlow,
	BusinessIndustry,
	BusinessSourceOfFunds,
	BusinessAccountPurpose,
	BusinessTransactionVolume,
	BusinessApplication,
	CustomerDueDiligence,
} from "../../../../resources/application"
import {Address, Phone, ErrorDocument} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import PhoneNumber from "../../../Address/PhoneNumber"
import {ApplicationResubmitForms} from "../../../../pages/ReevaluateApplication/ReevaluatePage"
import reevaluateBusinessValidationSchema from "./reevaluateBusinessValidationSchema"
import Dropdown from "../../../Address/Dropdown"
import CheckboxField from "../../../CheckboxField/CheckboxField"
import UsNexusFormField from "../UsNexusFormField"
import CountriesOfOperationFormField from "../CountriesOfOperationFormField"
import {isUndefined} from "lodash"

export type ReevaluatedBusinessApplicationDataRequest = Omit<
	ReevaluatedBusinessApplicationData & {website?: string | null},
	"hasNoWebsite"
>

export interface ReevaluatedBusinessApplicationData {
	name: string
	dba?: string
	address: Address
	operatingAddress?: Address
	ein: string
	phone: Phone
	businessVertical?: BusinessVertical | ""
	yearOfIncorporation: string
	hasNoWebsite: boolean
	website?: string
	stockSymbol?: string
	numberOfEmployees?: BusinessNumberOfEmployees | ""
	annualRevenue?: BusinessAnnualRevenue | ""
	cashFlow?: CashFlow | ""
	// EDD fields
	businessIndustry?: BusinessIndustry | ""
	sourceOfFunds?: BusinessSourceOfFunds | ""
	sourceOfFundsDescription?: string
	accountPurpose?: BusinessAccountPurpose | ""
	accountPurposeDetail?: string
	stockExchangeName?: string
	transactionVolume?: BusinessTransactionVolume | ""
	transactionVolumeDescription?: string
	isRegulated?: boolean
	regulatorName?: string
	usNexus?: string[]
	countriesOfOperation?: string[]
	businessDescription?: string
}

const validationSchema = Yup.object().shape(reevaluateBusinessValidationSchema)

export default function ReevaluateBusinessForm(props: {
	application: BusinessApplication
	businessApplication: ReevaluatedBusinessApplicationData
	submitCallback: (values: ReevaluatedBusinessApplicationData) => void
	formId: ApplicationResubmitForms
	error?: ErrorDocument
	onErrorDismiss: () => void
	orgId: string
}) {
	const {businessApplication, submitCallback, formId, error, onErrorDismiss} = props
	const [hasUserInteractedWithOperatingAddress, setHasUserInteractedWithOperatingAddress] = useState(false)
	const customerDueDiligence = props.application.attributes.customerDueDiligence

	const shouldShowExtendedDueDiligenceFields =
		(customerDueDiligence && customerDueDiligence.includes(CustomerDueDiligence.Version2026v1)) ?? false
	const isDefaultDueDiligenceFieldsAvailable =
		isUndefined(customerDueDiligence) ||
		customerDueDiligence.length === 0 ||
		customerDueDiligence.includes(CustomerDueDiligence.Default)

	useEffect(() => {
		if (error) {
			setHasUserInteractedWithOperatingAddress(false)
		}
	}, [error])

	const initialFormValues = {
		address: {
			street: businessApplication.address?.street,
			street2: businessApplication.address?.street2,
			city: businessApplication.address?.city,
			state: businessApplication.address?.state ?? "",
			postalCode: businessApplication.address?.postalCode,
			country: businessApplication.address?.country ?? US_COUNTRY_CODE,
		},
		ein: businessApplication.ein,
		phone: businessApplication.phone,
		name: businessApplication.name,
		website: businessApplication.website,
		dba: businessApplication.dba,
		businessVertical: businessApplication.businessVertical,
		yearOfIncorporation: businessApplication.yearOfIncorporation,
		numberOfEmployees: businessApplication.numberOfEmployees,
		annualRevenue: businessApplication.annualRevenue,
		stockSymbol: businessApplication.stockSymbol,
		cashFlow: businessApplication.cashFlow,
		hasNoWebsite: businessApplication.hasNoWebsite,
		operatingAddress: {
			street: businessApplication.operatingAddress?.street ?? "",
			street2: businessApplication.operatingAddress?.street2 ?? "",
			city: businessApplication.operatingAddress?.city ?? "",
			country: businessApplication.operatingAddress?.country ?? "US",
			state: businessApplication.operatingAddress?.state ?? "CA",
			postalCode: businessApplication.operatingAddress?.postalCode ?? "",
		},
		// EDD fields
		businessIndustry: businessApplication.businessIndustry,
		sourceOfFunds: businessApplication.sourceOfFunds,
		sourceOfFundsDescription: businessApplication.sourceOfFundsDescription,
		accountPurpose: businessApplication.accountPurpose,
		accountPurposeDetail: businessApplication.accountPurposeDetail,
		stockExchangeName: businessApplication.stockExchangeName,
		transactionVolume: businessApplication.transactionVolume,
		transactionVolumeDescription: businessApplication.transactionVolumeDescription,
		isRegulated: businessApplication.isRegulated,
		regulatorName: businessApplication.regulatorName,
		usNexus: businessApplication.usNexus,
		countriesOfOperation: businessApplication.countriesOfOperation,
		businessDescription: businessApplication.businessDescription,
	}

	const title = `Business - ${businessApplication.name}`

	const errorTitle = error?.errors?.[0]?.title
	const isOperatingAddressError = errorTitle?.includes("operatingAddress is required") ?? false
	// Insert spaces before capital letters in camelCase/PascalCase and lowercase the result
	const operatingAddressError = errorTitle?.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()
	const shouldShowAddressError = isOperatingAddressError && !hasUserInteractedWithOperatingAddress

	const handleOperatingAddressInteraction = () => {
		if (!hasUserInteractedWithOperatingAddress) {
			setHasUserInteractedWithOperatingAddress(true)
			onErrorDismiss()
		}
	}

	return (
		<Card className="reevaluate-form" title={title}>
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) => {
					const isOperatingAddressEmpty =
						!values.operatingAddress?.street?.trim() &&
						!values.operatingAddress?.street2?.trim() &&
						!values.operatingAddress?.city?.trim() &&
						!values.operatingAddress?.postalCode?.trim()

					submitCallback({
						...values,
						numberOfEmployees: values.numberOfEmployees === "" ? undefined : values.numberOfEmployees,
						annualRevenue: values.annualRevenue === "" ? undefined : values.annualRevenue,
						cashFlow: values.cashFlow === "" ? undefined : values.cashFlow,
						operatingAddress: isOperatingAddressEmpty ? undefined : values.operatingAddress,
						businessVertical: values.businessVertical === "" ? undefined : values.businessVertical,
						// EDD fields
						businessIndustry: values.businessIndustry === "" ? undefined : values.businessIndustry,
						sourceOfFunds: values.sourceOfFunds === "" ? undefined : values.sourceOfFunds,
						sourceOfFundsDescription:
							values.sourceOfFundsDescription === "" ? undefined : values.sourceOfFundsDescription,
						accountPurpose: values.accountPurpose === "" ? undefined : values.accountPurpose,
						accountPurposeDetail: values.accountPurposeDetail === "" ? undefined : values.accountPurposeDetail,
						transactionVolume: values.transactionVolume === "" ? undefined : values.transactionVolume,
						transactionVolumeDescription:
							values.transactionVolumeDescription === "" ? undefined : values.transactionVolumeDescription,
						isRegulated: values.isRegulated === undefined ? undefined : values.isRegulated,
						regulatorName: values.regulatorName === "" ? undefined : values.regulatorName,
						usNexus: values.usNexus?.length ? values.usNexus : undefined,
						countriesOfOperation: values.countriesOfOperation?.length ? values.countriesOfOperation : undefined,
						businessDescription: values.businessDescription === "" ? undefined : values.businessDescription,
						stockExchangeName: values.stockExchangeName === "" ? undefined : values.stockExchangeName,
					})
				}}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="Business Name"
											placeholder="Enter Business Name"
											name="name"
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<TextField
											label="EIN"
											placeholder="Enter EIN"
											name="ein"
											value={values.ein}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<TextField
											label="Doing Buisness As"
											placeholder="Enter dba"
											name="dba"
											value={values.dba ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>
										{isDefaultDueDiligenceFieldsAvailable && (
											<Dropdown
												name="businessVertical"
												label="Business Vertical"
												value={values.businessVertical}
												onChange={handleChange}
												onBlur={handleBlur}
											>
												{businessApplication.businessVertical ? <React.Fragment /> : <option value="" />}
												{Object.entries(BusinessVertical).map(([key, value]) => (
													<option key={key} value={key}>
														{value}
													</option>
												))}
											</Dropdown>
										)}
										<TextField
											label="Year Of Incorporation"
											placeholder="Enter Year Of Incorporation"
											name="yearOfIncorporation"
											value={values.yearOfIncorporation}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										{isDefaultDueDiligenceFieldsAvailable && (
											<Dropdown
												name="numberOfEmployees"
												label="Number Of Employees"
												value={values.numberOfEmployees}
												onChange={handleChange}
												onBlur={handleBlur}
											>
												{businessApplication.numberOfEmployees ? <React.Fragment /> : <option value="" />}
												{Object.entries(BusinessNumberOfEmployees).map(([key, value]) => (
													<option key={key} value={key}>
														{value}
													</option>
												))}
											</Dropdown>
										)}
										{isDefaultDueDiligenceFieldsAvailable && (
											<Dropdown
												name="annualRevenue"
												label="Annual Revenue"
												value={values.annualRevenue}
												onChange={handleChange}
												onBlur={handleBlur}
											>
												{businessApplication.annualRevenue ? <React.Fragment /> : <option value="" />}
												{Object.entries(BusinessAnnualRevenue).map(([key, value]) => (
													<option key={key} value={key}>
														{value}
													</option>
												))}
											</Dropdown>
										)}
										{isDefaultDueDiligenceFieldsAvailable && (
											<Dropdown
												name="cashFlow"
												label="Cash Flow"
												value={values.cashFlow}
												onChange={handleChange}
												onBlur={handleBlur}
											>
												{businessApplication.cashFlow ? <React.Fragment /> : <option value="" />}
												{Object.entries(CashFlow).map(([key, value]) => (
													<option key={key} value={key}>
														{value}
													</option>
												))}
											</Dropdown>
										)}

										{props.application.attributes.entityType === "PubliclyTradedCorporation" && (
											<TextField
												label="Stock Symbol"
												placeholder="Enter Stock Symbol"
												name="stockSymbol"
												value={values.stockSymbol ?? ""}
												onChange={(value) => {
													handleChange(value)
												}}
												onBlur={handleBlur}
											/>
										)}

										{shouldShowExtendedDueDiligenceFields &&
											props.application.attributes.entityType === "PubliclyTradedCorporation" && (
												<TextField
													label="Stock Exchange Name"
													placeholder="Enter Stock Exchange Name"
													name="stockExchangeName"
													value={values.stockExchangeName ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											)}

										<PhoneNumber
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone?.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>

										<div className={"website double-column-field"}>
											<TextField
												label="Website"
												placeholder="Enter website"
												name="website"
												value={values.website ?? ""}
												onChange={(value) => {
													handleChange(value)
												}}
												onBlur={handleBlur}
												disabled={values.hasNoWebsite}
											/>
											<CheckboxField
												name="hasNoWebsite"
												label="This business has no website"
												checked={values.hasNoWebsite}
												onBlur={handleBlur}
												onChange={(value) => {
													const isChecked = value.target.checked
													if (isChecked) {
														setFieldValue("website", "", true)
													} else {
														setFieldValue("website", businessApplication.website, true)
													}
													handleChange(value)
												}}
											/>
										</div>

										{shouldShowExtendedDueDiligenceFields && (
											<>
												<Dropdown
													name="businessIndustry"
													label="Business Industry"
													value={values.businessIndustry ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{businessApplication.businessIndustry ? <React.Fragment /> : <option value="" />}
													{Object.entries(BusinessIndustry).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>

												<Dropdown
													name="sourceOfFunds"
													label="Source Of Funds"
													value={values.sourceOfFunds ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{businessApplication.sourceOfFunds ? <React.Fragment /> : <option value="" />}
													{Object.entries(BusinessSourceOfFunds).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Source Of Funds Description"
														placeholder="Enter Source Of Funds Description"
														name="sourceOfFundsDescription"
														value={values.sourceOfFundsDescription ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<Dropdown
													name="accountPurpose"
													label="Account Purpose"
													value={values.accountPurpose ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{businessApplication.accountPurpose ? <React.Fragment /> : <option value="" />}
													{Object.entries(BusinessAccountPurpose).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Account Purpose Detail"
														placeholder="Enter Account Purpose Detail"
														name="accountPurposeDetail"
														value={values.accountPurposeDetail ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>

												<Dropdown
													name="transactionVolume"
													label="Transaction Volume"
													value={values.transactionVolume ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{businessApplication.transactionVolume ? <React.Fragment /> : <option value="" />}
													{Object.entries(BusinessTransactionVolume).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Transaction Volume Description"
														placeholder="Enter Transaction Volume Description"
														name="transactionVolumeDescription"
														value={values.transactionVolumeDescription ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>

												<Dropdown
													name="isRegulated"
													label="Is Regulated"
													value={values.isRegulated === true ? "yes" : values.isRegulated === false ? "no" : ""}
													onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
														const val = e.target.value
														setFieldValue("isRegulated", val === "yes" ? true : val === "no" ? false : undefined)
													}}
													onBlur={handleBlur}
												>
													<option value="" />
													<option value="yes">Yes</option>
													<option value="no">No</option>
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Regulator Name"
														placeholder="Enter Regulator Name"
														name="regulatorName"
														value={values.regulatorName ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<UsNexusFormField
													value={values.usNexus}
													onChange={(val) => setFieldValue("usNexus", val)}
													onBlur={handleBlur}
												/>

												<CountriesOfOperationFormField
													value={values.countriesOfOperation}
													onChange={(val) => setFieldValue("countriesOfOperation", val)}
												/>

												<div className="double-column-field">
													<TextField
														label="Business Description"
														placeholder="Enter Business Description"
														name="businessDescription"
														value={values.businessDescription ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
											</>
										)}
									</>
								}
								right={
									<>
										<AddressComponent
											border
											header="Legal Business Address"
											headerTooltip="The address that is listed on your business's official registration documents."
											closed={false}
											namePrefix="address."
											street={values.address.street}
											street2={values.address.street2}
											city={values.address.city}
											country={values.address.country || ""}
											state={values.address.state}
											postalCode={values.address.postalCode}
											isUsOnlyAddress={true}
											handleChange={handleChange}
											handleBlur={handleBlur}
											setFieldValue={setFieldValue}
										/>
										<AddressComponent
											border
											header="Physical Operating Address"
											headerTooltip="The office or residential address where you conduct your daily work."
											closed={false}
											namePrefix="operatingAddress."
											street={values.operatingAddress.street}
											street2={values.operatingAddress.street2}
											city={values.operatingAddress.city}
											country={values.operatingAddress.country}
											state={values.operatingAddress.state}
											postalCode={values.operatingAddress.postalCode}
											isUsOnlyAddress={false}
											handleChange={(e: any) => {
												handleOperatingAddressInteraction()
												handleChange(e)
											}}
											handleBlur={handleBlur}
											setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
												handleOperatingAddressInteraction()
												setFieldValue(field, value, shouldValidate)
											}}
											error={shouldShowAddressError}
											errorMessage={operatingAddressError}
										/>
									</>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
